import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './MeloAttributeDialog.less';
import Dialog from '../../../../components/envago/Dialog/Dialog';
import Button from '../../../../components/envago/Button/Button';
import Dropdown from '../../../../components/envago/Dropdown/Dropdown';
import ConfirmDialog from '../../../../components/envago/ConfirmDialog/ConfirmDialog';
import { MeloAttributeEntry, MeloAttributeItem } from '../../../../redux/jobs/jobs.types';
import { getMeloAttributeType } from './meloAttributeTypes';

export type MeloAttributeDialogMode = { kind: 'create' } | { kind: 'edit'; item: MeloAttributeItem };

interface MeloAttributeDialogProps {
    visible: boolean;
    mode: MeloAttributeDialogMode;
    availableTypes?: string[];
    saving?: boolean;
    errorMessage?: string;
    onClose: () => void;
    onCreate: (entry: MeloAttributeEntry) => void;
    onUpdate: (id: string, entry: MeloAttributeEntry, version: number) => void;
    onDelete: (id: string) => void;
}

const startType = (mode: MeloAttributeDialogMode, availableTypes: string[]): MeloAttributeEntry['type'] =>
    mode.kind === 'edit' ? mode.item.entry.type : (availableTypes[0] as MeloAttributeEntry['type']);

const MeloAttributeDialog = ({ visible, mode, availableTypes = ['NOTE'], saving, errorMessage, onClose, onCreate, onUpdate, onDelete }: MeloAttributeDialogProps) => {
    const { t } = useTranslation();

    const modeKey = mode.kind === 'edit' ? `edit:${mode.item.id}` : 'create';

    const [type, setType] = useState<MeloAttributeEntry['type']>(startType(mode, availableTypes));
    const [value, setValue] = useState<any>(getMeloAttributeType(startType(mode, availableTypes)).emptyValue);
    const [attemptFailed, setAttemptFailed] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const pendingSubmit = useRef(false);

    // (Re-)initialise the form whenever the dialog opens or targets another entry.
    useEffect(() => {
        if (!visible) return;
        const st = startType(mode, availableTypes);
        setType(st);
        setValue(mode.kind === 'edit' ? getMeloAttributeType(st).toValue(mode.item.entry) : getMeloAttributeType(st).emptyValue);
        setAttemptFailed(false);
        setConfirmDelete(false);
        pendingSubmit.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, modeKey]);

    // Close on success; keep open and surface the error on failure.
    useEffect(() => {
        if (!pendingSubmit.current || saving) return;
        pendingSubmit.current = false;
        if (errorMessage) {
            setAttemptFailed(true);
        } else {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saving, errorMessage]);

    const config = getMeloAttributeType(type);
    const canSave = !saving && config.isValid(value);

    const handleTypeChange = (next: string) => {
        const nextType = next as MeloAttributeEntry['type'];
        setType(nextType);
        setValue(getMeloAttributeType(nextType).emptyValue);
    };

    const handleSubmit = () => {
        if (!config.isValid(value)) return;
        const entry = config.toEntry(value);
        setAttemptFailed(false);
        pendingSubmit.current = true;
        if (mode.kind === 'edit') {
            onUpdate(mode.item.id, entry, mode.item.version);
        } else {
            onCreate(entry);
        }
    };

    const handleDeleteConfirmed = () => {
        if (mode.kind !== 'edit') return;
        setConfirmDelete(false);
        setAttemptFailed(false);
        pendingSubmit.current = true;
        onDelete(mode.item.id);
    };

    const typeValues = availableTypes.map((typeKey) => ({
        id: typeKey,
        value: typeKey,
        name: t(getMeloAttributeType(typeKey as MeloAttributeEntry['type']).labelKey),
    }));

    const actions = [
        <div
            key={'actions'}
            className={
                mode.kind === 'edit'
                    ? 'flex w-full items-center gap-2'
                    : 'flex w-full items-center gap-2 melo-attribute-dialog__actions--center'
            }
        >
            {mode.kind === 'edit' && (
                <>
                    <Button ghost onClick={() => setConfirmDelete(true)} disabled={saving} aria={{ 'aria-label': t('jobs.details.meloAttribute.delete') }}>
                        {t('jobs.details.meloAttribute.delete')}
                    </Button>
                    <div className={'flex-1'} />
                </>
            )}
            <Button primary onClick={handleSubmit} disabled={!canSave} isLoading={saving}>
                {t('jobs.details.meloAttribute.save')}
            </Button>
        </div>,
    ];

    return (
        <>
            <Dialog
                visible={visible}
                closable
                onClose={onClose}
                classNameContent={'melo-attribute-dialog'}
                title={t(mode.kind === 'edit' ? 'jobs.details.meloAttribute.dialog.editTitle' : 'jobs.details.meloAttribute.dialog.addTitle')}
                actions={actions}
            >
                <div className={'flex flex-col gap-3'}>
                    <p className={'text-sm text-gray-500'}>{t('jobs.details.meloAttribute.dialog.description')}</p>
                    {mode.kind === 'create' && availableTypes.length > 1 && (
                        <Dropdown values={typeValues} value={type} onChange={handleTypeChange} placeholder={t('jobs.details.meloAttribute.dialog.addTitle')} />
                    )}
                    {config.renderForm({ value, onChange: setValue, t })}
                    {attemptFailed && errorMessage && (
                        <div className={'text-red-500'} role={'alert'}>
                            {errorMessage}
                        </div>
                    )}
                </div>
            </Dialog>

            <ConfirmDialog
                open={confirmDelete}
                title={t('jobs.details.meloAttribute.deleteConfirm.title')}
                content={t('jobs.details.meloAttribute.deleteConfirm.content')}
                okText={t('jobs.details.meloAttribute.deleteConfirm.ok')}
                cancelText={t('jobs.details.meloAttribute.deleteConfirm.cancel')}
                onOk={handleDeleteConfirmed}
                onCancel={() => setConfirmDelete(false)}
            />
        </>
    );
};

export default MeloAttributeDialog;
